// Flows automation failures (part 3 of 3 — the catalog/toggle/audit log was
// part 1, the auto-execution engine was part 2). Lists every unresolved
// automated-processing failure so an admin can see what needs manual
// handling and either jump to the conversation or dismiss it once handled.
// 2026-10-22: each row says what it is (Failure / Partial: <what> / Completed
// with fallback), the step that failed, the steps that succeeded before it,
// the full saved error (status and the provider's redacted response body),
// and "Handled since by a person" (what and when, never who). A row stays
// until an admin resolves it.
// 2026-10-29: two filters, "Failures" and "Completed another way" (a run
// that finished by its fallback); the second is never mixed in with the
// failures and, as before, never counted in the top-nav count.
import { useEffect, useState, useCallback } from 'react'
import { Box, Stack, Typography, Chip, Button, Paper, CircularProgress, Alert, IconButton, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { fetchAutomationFailures, resolveAutomationFailure, recordReturnLabelAgain } from '../../api.js'
import { RecordLabelAgainButton } from '../../ReturnLabelPending.jsx'
import { outcomeLabel, OUTCOME_COLORS, errorSummary, errorBodyText, succeededStepsText, FAILURE_FILTERS, filterFailures, failureFilterCounts } from '../../failureLabels.js'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) + ' ET'
}

function FailureDetail({ failure }) {
  const [showBody, setShowBody] = useState(false)
  const succeeded = succeededStepsText(failure.succeededSteps)
  const summary = errorSummary(failure.errorDetail)
  const bodyText = errorBodyText(failure.errorDetail)
  if (!failure.failedStep && !succeeded.length && !summary && !bodyText) return null
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {failure.failedStep && (
        <Typography variant="body2"><strong>{failure.outcome === 'fallback' ? 'Fell back:' : 'Not done:'}</strong> {failure.failedStep}</Typography>
      )}
      {succeeded.length > 0 && (
        <Typography variant="body2"><strong>Done before it:</strong> {succeeded.join('; ')}</Typography>
      )}
      {(summary || bodyText) && (
        <Stack direction="row" spacing={1} alignItems="center">
          {summary && <Typography variant="body2"><strong>Error:</strong> {summary}</Typography>}
          {bodyText && (
            <Button size="small" onClick={() => setShowBody((v) => !v)} sx={{ minWidth: 0, p: 0 }}>
              {showBody ? 'Hide response' : 'Show response'}
            </Button>
          )}
        </Stack>
      )}
      {showBody && bodyText && (
        <Box component="pre" data-testid="failure-error-body" sx={{ m: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto' }}>{bodyText}</Box>
      )}
    </Stack>
  )
}

export default function FailuresPage({ onOpenInAgent, onResolved }) {
  const [failures, setFailures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [resolvingId, setResolvingId] = useState(null)
  const [filter, setFilter] = useState('failures')

  const load = useCallback(() => {
    setLoading(true); setError(null)
    fetchAutomationFailures()
      .then(setFailures)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  // "Record the label again" (2026-11-02) on a return-label entry whose label
  // was made but not recorded: worked, the entry is resolved and leaves the
  // list; failed again, it stays with the button.
  async function handleRecordAgain(pendingId) {
    const out = await recordReturnLabelAgain(pendingId)
    load()
    if (out?.outcome === 'done') onResolved?.()
    return out
  }

  async function handleResolve(id) {
    setResolvingId(id)
    try {
      await resolveAutomationFailure(id)
      setFailures((prev) => prev.filter((f) => f.id !== id))
      onResolved?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setResolvingId(null)
    }
  }

  const counts = failureFilterCounts(failures)
  const shown = filterFailures(failures, filter)

  return (
    <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Automation Failures</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Suggestions whose flow is set to "Automate" but whose automated run didn't fully succeed — each needs manual handling. A partial run did some steps and not others. A run completed with fallback finished another way: it is under "Completed another way", not counted. Resolving just dismisses the entry here; it never re-runs automation.
      </Typography>
      <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => { if (v) setFilter(v) }} aria-label="Failures filter" sx={{ mb: 2 }}>
        {FAILURE_FILTERS.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value} sx={{ textTransform: 'none' }}>{`${opt.label} (${counts[opt.value] ?? 0})`}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : shown.length === 0 ? (
        <Typography color="text.secondary">{filter === 'completed_another_way' ? 'No unresolved runs completed another way.' : 'No unresolved automation failures.'}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {shown.map((f) => (
            <Paper key={f.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={OUTCOME_COLORS[f.outcome ?? 'failure'] ?? 'error'} label={outcomeLabel(f)} />
                    <Typography fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 13 }}>{f.flowKey}</Typography>
                    {f.conversationChannel && <Chip size="small" variant="outlined" label={f.conversationChannel} />}
                    <Typography variant="caption" color="text.secondary">{formatDate(f.createdAt)}</Typography>
                  </Stack>
                  <Typography variant="body2">{f.failureMessage}</Typography>
                  <FailureDetail failure={f} />
                  {f.handledSince?.label && (
                    <Chip size="small" color="success" variant="outlined" sx={{ alignSelf: 'flex-start' }} label={f.handledSince.label} />
                  )}
                  <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                    {f.conversationConvNumber != null && (
                      <Button
                        size="small" variant="outlined" sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                        onClick={() => onOpenInAgent?.(f.conversationId, f.conversationConvNumber)}
                      >
                        Open in agent view
                      </Button>
                    )}
                    {f.recordAgain && (
                      <Stack spacing={0.5} data-testid="failure-record-again">
                        <RecordLabelAgainButton
                          pendingId={f.recordAgain.pendingReturnLabelId}
                          info={f.recordAgain}
                          conversationId={f.conversationId}
                          onRecordAgain={handleRecordAgain}
                          onOpenConversation={(id, n) => onOpenInAgent?.(id, n)}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Stack>
                <Tooltip title="Resolve (dismiss)">
                  <span>
                    <IconButton
                      size="small"
                      disabled={resolvingId === f.id}
                      onClick={() => handleResolve(f.id)}
                    >
                      {resolvingId === f.id ? <CircularProgress size={16} /> : '✕'}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  )
}
